/**
 * @description
 * 1. 初始化
 * 2. 拉起支付
 */

import { computed, ref, watch } from "@vue/composition-api"
import * as config from "../config"
import cookies from "../../../utils/cookies"
import { AppleGiftCardActions, PayProps } from "../store/apple-gift-card/actions"
import { CONTRACT_STATUS, HOME_PAGE_LAST_BUY_TAB, CONTRACT_STATUS_COPYWRITING } from "../constant"
import { createNamespacedHelpers } from "vuex-composition-helpers"
import MUTATION_TYPES from "../store/apple-gift-card/mutation-types"
import { AppleGiftCardGetters } from "../store/apple-gift-card/getters"
import { AppleGiftCardMutations } from "../store/apple-gift-card/mutations"
import { AppleGiftCardState, UsingLocation } from "../store/apple-gift-card/state"
import useLocation from "./useLocation"
import { getAuthPrivacy } from "../../../api/privacy"

const { useActions, useGetters, useMutations, useState } = createNamespacedHelpers<
  AppleGiftCardState,
  AppleGiftCardGetters,
  AppleGiftCardActions,
  AppleGiftCardMutations
>("appleGiftCard")

export interface IndexLoginSession {
  openid: string
  openkey: string
  sessionId: string
  sessionType: string
  wxAppid2?: string
  qqAppid?: string
}

interface InitProps {
  showAppleAuth: () => void
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default ({ showAppleAuth }: InitProps) => {
  const { toRechargePage } = useLocation()
  const { banners, loginState } = useGetters(["banners", "loginState"])
  const { isLoading, configInfo, selectedCoupon, agreeChecked, toastMsg, toastMsgTimer, notSign, usingLocation } = useState([
    "isLoading",
    "configInfo",
    "selectedCoupon",
    "agreeChecked",
    "toastMsg",
    "toastMsgTimer",
    "notSign",
    "usingLocation"
  ])
  const {
    getLoginState,
    querySignContract,
    placeOrder,
    pay,
    wechatPay,
    reportUserAction,
    queryDRMLimit,
    queryUserAvailableCoupons,
    setToastMsg,
    getAuthPrivacy,
    setAuthPrivacy,
    checkUserAgreedAuth,
    getActivityByUserType
  } = useActions([
    "getLoginState",
    "querySignContract",
    "placeOrder",
    "pay",
    "wechatPay",
    "reportUserAction",
    "queryDRMLimit",
    "queryUserAvailableCoupons",
    "setToastMsg",
    "getAuthPrivacy",
    "checkUserAgreedAuth",
    "setAuthPrivacy",
    "getActivityByUserType"
  ])

  const mutations = useMutations([
    MUTATION_TYPES.SET_HIDE_WARN,
    MUTATION_TYPES.SET_MASK_ID,
    MUTATION_TYPES.SET_IS_LOADING,
    MUTATION_TYPES.SET_LOGIN_STATE,
    MUTATION_TYPES.SET_NOT_SIGN,
    MUTATION_TYPES.SET_IS_LOGGED_IN,
    MUTATION_TYPES.SET_USING_LOCATION
  ])

  const signStatus = ref<CONTRACT_STATUS>(CONTRACT_STATUS.UNKNOWN)
  const signErrorMsg = ref("")
  const loginErrorMsg = ref("")
  const loginStateFailure = ref(false)
  const checkSignError = ref(false)
  const selectedPayNum = ref(0)
  const drmLimitMsg = ref("")
  const payErrorMsg = ref("")
  const isAppleCardGiftLocation = computed(() => usingLocation.value === UsingLocation.APPLE_GIFT_CARD)

  const startPay = async (num: number, groupid?: string) => {
    reportUserAction({
      iformat: isAppleCardGiftLocation.value ? config.REPORT.CLICK.PAY : config.REPORT.CLICK[usingLocation.value].PAY,
      res: num
    })
    if (!agreeChecked.value) {
      mutations[MUTATION_TYPES.SET_HIDE_WARN](false)
      setToastMsg({ toastMsg: "请先勾选服务协议" })
      return
    }
    if (num < 6 || num > 1000) {
      setToastMsg({ toastMsg: "请输入6-1000的整数" })
      return
    }
    selectedPayNum.value = num
    if (notSign.value) {
      // 记录用户点击的档位，存到cookie中
      try {
        if (isAppleCardGiftLocation.value) {
          cookies.set("apple_card_pay_num", JSON.stringify({ num, groupid }))
          showAppleAuth()
        } else {
          toRechargePage()
        }
      } catch (e) {
        reportUserAction({
          iformat: config.REPORT.PAGEVIEW.GET_WECHAT_REALNAME_INFO_ERROR,
          resultInfo: e
        })
      }
    } else {
      mutations[MUTATION_TYPES.SET_IS_LOADING](true)
      try {
        // 获取订单token
        const placeOrderRes = await placeOrder({
          quantity: (num * 10000) / 100,
          groupid
        })

        const params: PayProps = {
          mid: placeOrderRes.mid,
          token_id: placeOrderRes.token
        }

        /**
         * 抵扣券和全场折扣可能同时存在
         * 若同时存在的话，抵扣券优先
         */
        if (selectedCoupon.value && num >= selectedCoupon.value.threshold) {
          // 抵扣券参数
          params.codeId = selectedCoupon.value.codeId
          params.batchId = selectedCoupon.value.batchId
        } else if (groupid) {
          params.groupid = groupid
        }

        // Portal、微信下单
        const payRes = await pay(params)
        mutations[MUTATION_TYPES.SET_IS_LOADING](false)

        // 拉起微信支付
        await wechatPay(payRes.wx_info)

        // 支付结果展示
        reportUserAction({
          iformat: config.REPORT.PAGEVIEW.PAY_SUCCESS
        })

        // 同意充值协议缓存
        setAuthPrivacy()
        // localStorage.setItem("apple-gift-card__agreeChecked", "apple-gift-card__agreeChecked")
        if (usingLocation.value === UsingLocation.HOME) {
          // 充值tab缓存
          localStorage.setItem("home-page__last-buy-tab", HOME_PAGE_LAST_BUY_TAB.APPLE_GIFT_CARD)
        }
      } catch (e: any) {
        if (e && e.err_msg === "get_brand_wcpay_request:cancel") {
          // 用户取消支付
          mutations[MUTATION_TYPES.SET_IS_LOADING](false)
          return
        }

        if (e && e.msg && e.err_code == "") {
          if (e.msg == configInfo.value?.drmLimit?.cgiMsg) {
            // 用户活动无资格让用户原价购买
            drmLimitMsg.value = configInfo.value?.drmLimit?.guideMsg
          } else {
            payErrorMsg.value = e.msg
          }
        } else {
          payErrorMsg.value = "活动过于火爆，收银小姐姐忙，请稍后重试!"
        }
        reportUserAction({
          iformat: config.REPORT.PAGEVIEW.PLACE_ORDER_ERROR,
          resultInfo: e
        })
      }
      mutations[MUTATION_TYPES.SET_IS_LOADING](false)
    }
  }

  const init = async (doModuleGetLoginState: boolean) => {
    try {
      if (doModuleGetLoginState) {
        // 使用module store内的getLoginState
        await getLoginState()
      }
      mutations[MUTATION_TYPES.SET_IS_LOGGED_IN](true)
      reportUserAction({
        iformat: isAppleCardGiftLocation.value ? config.REPORT.PAGEVIEW.HOME_PAGE : config.REPORT.PAGEVIEW[usingLocation.value].HOME_PAGE
      })

      //对于大页面和首页tab采取不通的获取用户是否授权的方式
      if (usingLocation.value == UsingLocation.APPLE_GIFT_CARD) {
        getAuthPrivacy()
      } else if (usingLocation.value == UsingLocation.HOME) {
        checkUserAgreedAuth()
      }
      const userTypeActivity = await getActivityByUserType()
      queryDRMLimit(userTypeActivity)
      queryUserAvailableCoupons()
      const res = await querySignContract() // 查询签约资格
      const paynum: {
        num: number
        groupid: string
      } = JSON.parse(cookies.get("apple_card_pay_num") || "{}")
      signStatus.value = res.info.contract_status
      if (res.info.contract_status === CONTRACT_STATUS.APPLE_AUTH_NOT_MATCH) {
        mutations[MUTATION_TYPES.SET_NOT_SIGN](true)
        checkSignError.value = true
        signErrorMsg.value = CONTRACT_STATUS_COPYWRITING[CONTRACT_STATUS.APPLE_AUTH_NOT_MATCH]
        reportUserAction({
          iformat: config.REPORT.PAGEVIEW.APPLE_SIGN_NOT_MATCH,
          resultInfo: res
        })
      } else if (res.info.contract_status === CONTRACT_STATUS.APPLE_AUTH_NOT_MATCH_INVALID) {
        mutations[MUTATION_TYPES.SET_NOT_SIGN](true)
        checkSignError.value = true
        signErrorMsg.value = CONTRACT_STATUS_COPYWRITING[CONTRACT_STATUS.APPLE_AUTH_NOT_MATCH_INVALID]
        reportUserAction({
          iformat: config.REPORT.PAGEVIEW.APPLE_AUTH_NOT_MATCH_INVALID,
          resultInfo: res
        })
      } else if (res.info.contract_status != CONTRACT_STATUS.SIGN) {
        /**
         * 如果未签约，但cookie里面却有购买数量
         * 说明用户是点击了档位，跳转到app store后拒绝签约后跳转回来的
         * 这时再次让他进行签约
         */
        reportUserAction({
          iformat: config.REPORT.PAGEVIEW.USER_NO_SIGN,
          resultInfo: res
        })
        mutations[MUTATION_TYPES.SET_NOT_SIGN](true)
        if (paynum.num > 0 && isAppleCardGiftLocation.value) {
          try {
            showAppleAuth()
          } catch (e) {
            reportUserAction({
              iformat: config.REPORT.PAGEVIEW.GET_WECHAT_REALNAME_INFO_ERROR,
              resultInfo: e
            })
          }
          return
        }
      } else {
        reportUserAction({
          iformat: config.REPORT.PAGEVIEW.USER_ALREADY_SIGN,
          resultInfo: res
        })
        mutations[MUTATION_TYPES.SET_MASK_ID](res.info.mask_id)
        // 已签约，可以让用户购买
        mutations[MUTATION_TYPES.SET_NOT_SIGN](false)
        if (paynum.num > 0) {
          // 拉起支付，并且删掉cookie
          await startPay(paynum.num, paynum.groupid)
          cookies.del("apple_card_pay_num")
        }
      }
    } catch (e: any) {
      // 查签约状态异常或者获取登陆态失败
      if (e.err) {
        loginErrorMsg.value = e.msg
        loginStateFailure.value = e.err === 8888 || e.err === 1111
        if (loginStateFailure.value) {
          reportUserAction({
            iformat: config.REPORT.PAGEVIEW.LOGIN_ERROR,
            resultInfo: e
          })
        }
      } else {
        reportUserAction({
          iformat: config.REPORT.PAGEVIEW.CHECK_SIGN_ERROR,
          resultInfo: e
        })
        checkSignError.value = true
        signErrorMsg.value = CONTRACT_STATUS_COPYWRITING.error
      }
    }
  }

  watch(agreeChecked, () => {
    if (agreeChecked.value && toastMsgTimer.value) {
      setToastMsg({ toastMsg: "" })
    }
  })

  return {
    banners,
    signStatus,
    signErrorMsg,
    loginErrorMsg,
    loginStateFailure,
    isLoading,
    checkSignError,
    drmLimitMsg,
    payErrorMsg,
    selectedPayNum,
    configInfo,
    loginState,
    notSign,
    mutations,
    toastMsg,
    init,
    startPay
  }
}
